'use client';

import dynamic from 'next/dynamic';
import { useCallback, useId, useRef, useState, type ReactNode } from 'react';
import type { VehicleCardModel } from '@/features/vehicle';
import type { HeadingLevel } from '@/lib/utils/headingLevel';
import { hasActiveFilters, type VehicleFilters } from './vehicleFilters';
import { liveLabel } from './vehiclePagination';
import { useStickySidebar } from './useStickySidebar';
import { useVehicleListing } from './useVehicleListing';
import { VehicleActiveFilters } from './VehicleActiveFilters';
import { VehicleEmptyState } from './VehicleEmptyState';
import { VehicleFilterButton } from './VehicleFilterButton';
import { VehicleFilterPanel } from './VehicleFilterPanel';
import { VehicleListingToolbar } from './VehicleListingToolbar';
import { VehiclePagination } from './VehiclePagination';
import { VehicleResults } from './VehicleResults';

// Only a phone visitor who opens the filters needs the dialog code.
const VehicleFilterDrawer = dynamic(
    () => import('./VehicleFilterDrawer').then((module) => module.VehicleFilterDrawer),
    {
        ssr: false,
    },
);

/** Clear of the sticky header when a page change scrolls back up. */
const SCROLL_OFFSET = 100;

function scrollToTop(element: HTMLElement | null) {
    if (!element) return;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const top = element.getBoundingClientRect().top + window.scrollY - SCROLL_OFFSET;

    window.scrollTo({ top, behavior: reduce ? 'auto' : 'smooth' });
}

type VehicleListingBrowserProps = {
    vehicles: VehicleCardModel[];
    headingLevel: HeadingLevel;
    emptyAction?: ReactNode;
};

/** Filters, sort, view and pages over the whole stock — all in the browser, no round trip. */
export function VehicleListingBrowser({ vehicles, headingLevel, emptyAction }: VehicleListingBrowserProps) {
    const listing = useVehicleListing(vehicles);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerMounted, setDrawerMounted] = useState(false);
    const snapshotRef = useRef<VehicleFilters>(listing.filters);
    const resultsRef = useRef<HTMLDivElement>(null);
    const sidebarRef = useRef<HTMLElement>(null);
    const sidebarTitleId = useId();
    const drawerId = useId();

    useStickySidebar(sidebarRef);

    const { changeFilters, setPage } = listing;

    const openDrawer = () => {
        snapshotRef.current = listing.filters;
        setDrawerMounted(true);
        setDrawerOpen(true);
    };

    const closeDrawer = useCallback(() => setDrawerOpen(false), []);

    const cancelDrawer = useCallback(() => {
        changeFilters(snapshotRef.current);
        setDrawerOpen(false);
    }, [changeFilters]);

    const goToPage = (page: number) => {
        setPage(page);
        scrollToTop(resultsRef.current);
    };

    const panel = {
        filters: listing.filters,
        options: listing.options,
        bounds: listing.bounds,
        onChange: changeFilters,
        onReset: listing.resetFilters,
        hasActiveFilters: hasActiveFilters(listing.filters),
    };

    return (
        <div className="vehicle-listing">
            <aside ref={sidebarRef} className="vehicle-listing__filters" aria-labelledby={sidebarTitleId}>
                <VehicleFilterPanel {...panel} titleId={sidebarTitleId} />
            </aside>

            <div ref={resultsRef} className="vehicle-listing__main">
                <VehicleListingToolbar
                    total={listing.total}
                    sort={listing.sort}
                    onSort={listing.changeSort}
                    view={listing.view}
                    onView={listing.setView}
                />

                <VehicleActiveFilters
                    chips={listing.chips}
                    onRemove={listing.removeChip}
                    onReset={listing.resetFilters}
                />

                {listing.total ? (
                    <>
                        <VehicleResults
                            vehicles={listing.pageVehicles}
                            view={listing.view}
                            headingLevel={headingLevel}
                        />
                        <VehiclePagination
                            pagination={listing.pagination}
                            total={listing.total}
                            perPage={listing.perPage}
                            onPage={goToPage}
                            onPerPage={listing.setPerPage}
                        />
                    </>
                ) : (
                    <VehicleEmptyState
                        headingLevel={headingLevel}
                        onReset={listing.resetFilters}
                        action={emptyAction}
                    />
                )}

                <p className="sr-only" aria-live="polite">
                    {liveLabel(listing.pagination, listing.total)}
                </p>
            </div>

            <VehicleFilterButton
                activeCount={listing.chips.length}
                expanded={drawerOpen}
                controls={drawerId}
                onOpen={openDrawer}
            />

            {drawerMounted ? (
                <VehicleFilterDrawer
                    id={drawerId}
                    open={drawerOpen}
                    panel={panel}
                    view={listing.view}
                    onView={listing.setView}
                    total={listing.total}
                    onClose={closeDrawer}
                    onCancel={cancelDrawer}
                />
            ) : null}
        </div>
    );
}
